import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import {
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarMonthIcon,
  CalendarViewMonthOutlined as CalendarViewMonthOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  LocalGasStationOutlined as LocalGasStationOutlinedIcon,
  OpacityOutlined as OpacityOutlinedIcon,
  Schedule as ScheduleIcon,
  ViewModule as StackerViewIcon,
  VisibilityOffOutlined as VisibilityOffOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from "@mui/icons-material";

import {
  addDays,
  addWeeks,
  endOfWeek,
  format,
  getWeek,
  startOfWeek,
  subWeeks,
} from "date-fns";

import type { Locale } from "date-fns";
import { de, enUS, fr, nl } from "date-fns/locale";

import Layout from "../../Layout.tsx";
import GasoilYearOverviewDialog from "../../vessel/gasoil/GasoilYearOverviewDialog";

const WRAPPER_BORDER_RADIUS = "10px !important";
const GASOIL_COLOR = "#0B2E78";
const BUNKERING_COLOR = "#16a34a";
const CARGO_COLOR = "#7c3aed";
const LOADING_COLOR = "#1976d2";
const UNLOADING_COLOR = "#9333ea";

type ReportDateTime = {
  date: string;
  dateFull: string;
  time: string;
  iso: string;
};

type ReportStatusKey = "completed" | "ongoing" | "planned" | "to_plan";

type WeeklyReportRow = {
  id: number;
  "@id": string;
  date: string | null;
  dateIso: string | null;
  day: string | null;
  vessel?: {
    id: number | null;
    "@id": string | null;
    name: string | null;
  };
  terminal: {
    id: number | null;
    "@id": string | null;
    name: string | null;
    abbreviation: string | null;
    city: string | null;
    color: string | null;
  };
  planned: ReportDateTime | null;
  arrival: ReportDateTime | null;
  operationStart: ReportDateTime | null;
  operationEnd: ReportDateTime | null;
  departure: ReportDateTime | null;
  loading: {
    containers: number;
    teu: number;
  };
  unloading: {
    containers: number;
    teu: number;
  };
  status: {
    key: ReportStatusKey;
    label: string;
  };
};

type WeeklyGasoilMeasurement = {
  id: number;
  "@id": string;
  amount: number;
  date: string | null;
  updatedAt?: string | null;
};

type WeeklyGasoilBunkering = {
  id: number;
  "@id": string;
  amount: number;
  date: string | null;
  supplier: string | null;
};

type WeeklyVesselGasoilSummary = {
  vessel: {
    id: number;
    "@id": string;
    name: string | null;
  };
  openingOnboard: WeeklyGasoilMeasurement | null;
  closingOnboard: WeeklyGasoilMeasurement | null;
  bunkered: number;
  consumption: number | null;
  measurementsCount: number;
  bunkeringsCount: number;
  measurements: WeeklyGasoilMeasurement[];
  bunkerings: WeeklyGasoilBunkering[];
};

type WeeklyGasoilSummary = {
  openingOnboard: number | null;
  closingOnboard: number | null;
  bunkered: number;
  consumption: number | null;
  measurementsCount: number;
  bunkeringsCount: number;
  hasCompleteConsumptionData: boolean;
  vessels: WeeklyVesselGasoilSummary[];
};

type WeeklyReportTotals = {
  loadingContainers: number;
  loadingTeu: number;
  unloadingContainers: number;
  unloadingTeu: number;
};

type WeeklyReportResponse = {
  weekStart: string;
  weekEnd: string;
  weekLabel: string;
  vesselName: string | null;
  rows: WeeklyReportRow[];
  totals: WeeklyReportTotals;
  gasoil: WeeklyGasoilSummary;
};

type YearOverviewVessel = {
  id: number;
  "@id": string;
  name: string | null;
};

type YearOverviewMeasurement = {
  id: number;
  "@id": string;
  amount: number;
  date: string;
  vessel: YearOverviewVessel;
};

type YearOverviewBunkering = {
  id: number;
  "@id": string;
  amount: number;
  date: string;
  supplier: string | null;
  vessel: YearOverviewVessel;
};

type YearOverviewDay = {
  date: string;
  measurements: YearOverviewMeasurement[];
  bunkerings: YearOverviewBunkering[];
};

type YearOverviewWeek = {
  week: number;
  weekYear: number;
  weekStart: string;
  weekEnd: string;
  days: YearOverviewDay[];
  openingOnboard: {
    amount: number;
    date: string;
  } | null;
  closingOnboard: {
    amount: number;
    date: string;
  } | null;
  bunkered: number;
  consumption: number | null;
  hasCompleteConsumptionData: boolean;
  measurementsCount: number;
  bunkeringsCount: number;
};

export type YearOverviewResponse = {
  year: number;
  timezone: string;
  generatedAt: string;
  weeks: YearOverviewWeek[];
};

type SortOrder = "asc" | "desc";

type SortKey =
  | "date"
  | "terminal"
  | "planned"
  | "arrival"
  | "operationStart"
  | "operationEnd"
  | "departure"
  | "loadingContainers"
  | "loadingTeu"
  | "unloadingContainers"
  | "unloadingTeu"
  | "status";

type TerminalTimelineRow = {
  type: "terminal";
  key: string;
  timestamp: number;
  row: WeeklyReportRow;
};

type GasoilTimelineRow = {
  type: "gasoil";
  key: string;
  timestamp: number;
  date: string;
  vessel: WeeklyVesselGasoilSummary["vessel"];
  measurement: WeeklyGasoilMeasurement;
};

type BunkeringTimelineRow = {
  type: "bunkering";
  key: string;
  timestamp: number;
  date: string;
  vessel: WeeklyVesselGasoilSummary["vessel"];
  bunkering: WeeklyGasoilBunkering;
};

type WeeklyTimelineRow =
  | TerminalTimelineRow
  | GasoilTimelineRow
  | BunkeringTimelineRow;

const getDateFnsLocale = (language?: string): Locale => {
  const normalizedLanguage = String(language || "en").toLowerCase();

  if (normalizedLanguage.startsWith("nl")) {
    return nl;
  }

  if (normalizedLanguage.startsWith("fr")) {
    return fr;
  }

  if (normalizedLanguage.startsWith("de")) {
    return de;
  }

  return enUS;
};

const parseIsoDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const parseDateIsoOnly = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const formatWeekRange = (weekStart: Date, locale: Locale): string => {
  const weekEnd = endOfWeek(weekStart, {
    weekStartsOn: 1,
  });

  return `${format(weekStart, "dd MMM", {
    locale,
  })} – ${format(weekEnd, "dd MMM yyyy", {
    locale,
  })}`;
};

const formatDisplayDate = (
  dateIso: string | null | undefined,
  locale: Locale
): string => {
  const date = parseDateIsoOnly(dateIso);

  if (!date) {
    return "-";
  }

  return format(date, "dd MMM", {
    locale,
  });
};

const formatDisplayDay = (
  dateIso: string | null | undefined,
  fallback: string | null | undefined,
  locale: Locale
): string => {
  const date = parseDateIsoOnly(dateIso);

  if (!date) {
    return fallback || "-";
  }

  return format(date, "EEEE", {
    locale,
  });
};

const formatDisplayDateTimeDate = (
  value: ReportDateTime | null,
  locale: Locale
): string => {
  const date = parseIsoDate(value?.iso);

  if (!date) {
    return value?.date ?? "";
  }

  return format(date, "dd MMM", {
    locale,
  });
};

const formatTimelineDate = (
  value?: string | null,
  locale: Locale = enUS
): string => {
  const date = parseIsoDate(value);

  if (!date) {
    return "—";
  }

  return format(date, "dd MMM yyyy", {
    locale,
  });
};

const formatTimelineDay = (
  value?: string | null,
  locale: Locale = enUS
): string => {
  const date = parseIsoDate(value);

  if (!date) {
    return "—";
  }

  return format(date, "EEEE", {
    locale,
  });
};

const formatTimelineTime = (value?: string | null): string => {
  const date = parseIsoDate(value);

  if (!date) {
    return "—";
  }

  return format(date, "HH:mm");
};

const formatCsvDate = (value?: string | null): string => {
  const date = parseDateIsoOnly(value);

  if (!date) {
    return value || "";
  }

  return format(date, "yyyy-MM-dd");
};

const formatCsvDay = (
  dateIso: string | null | undefined,
  fallback: string | null | undefined,
  locale: Locale
): string => {
  const date = parseDateIsoOnly(dateIso);

  if (!date) {
    return fallback || "";
  }

  return format(date, "EEEE", {
    locale,
  });
};

const formatCsvDateTime = (value: ReportDateTime | null): string => {
  const date = parseIsoDate(value?.iso);

  if (!date) {
    return "";
  }

  return format(date, "yyyy-MM-dd HH:mm");
};

const formatCsvIsoDateTime = (value?: string | null): string => {
  const date = parseIsoDate(value);

  if (!date) {
    return "";
  }

  return format(date, "yyyy-MM-dd HH:mm");
};

const gasoilNumberFormat = new Intl.NumberFormat("nl-BE", {
  maximumFractionDigits: 2,
});

const formatGasoilNumber = (value?: number | null): string => {
  if (value === null || value === undefined) {
    return "";
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return "";
  }

  return gasoilNumberFormat.format(parsed);
};

const formatGasoilAmount = (value?: number | null): string => {
  const formatted = formatGasoilNumber(value);

  return formatted ? `${formatted} L` : "—";
};

const formatGasoilMeasurementDate = (
  value?: string | null,
  locale: Locale = enUS
): string => {
  const date = parseIsoDate(value);

  if (!date) {
    return "—";
  }

  return format(date, "dd MMM yyyy HH:mm", {
    locale,
  });
};

const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || "";
};

const getTimeValue = (value: ReportDateTime | null): number => {
  if (!value?.iso) {
    return 0;
  }

  const timestamp = new Date(value.iso).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getValidTimestamp = (value?: string | null): number => {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getPrimaryDateSortValue = (row: WeeklyReportRow): number => {
  return getTimeValue(row.arrival) || getTimeValue(row.planned);
};

const getTerminalTimelineTimestamp = (row: WeeklyReportRow): number => {
  return (
    getTimeValue(row.arrival) ||
    getTimeValue(row.planned) ||
    getTimeValue(row.operationStart) ||
    getTimeValue(row.operationEnd) ||
    getTimeValue(row.departure)
  );
};

const normalizeString = (value: any): string => {
  return String(value ?? "").trim().toLowerCase();
};

const getSortValue = (
  row: WeeklyReportRow,
  sortKey: SortKey
): string | number => {
  switch (sortKey) {
    case "date":
      return getPrimaryDateSortValue(row);

    case "terminal":
      return normalizeString(row.terminal.name);

    case "planned":
      return getTimeValue(row.planned);

    case "arrival":
      return getTimeValue(row.arrival);

    case "operationStart":
      return getTimeValue(row.operationStart);

    case "operationEnd":
      return getTimeValue(row.operationEnd);

    case "departure":
      return getTimeValue(row.departure);

    case "loadingContainers":
      return row.loading.containers;

    case "loadingTeu":
      return row.loading.teu;

    case "unloadingContainers":
      return row.unloading.containers;

    case "unloadingTeu":
      return row.unloading.teu;

    case "status":
      return normalizeString(row.status.key);

    default:
      return "";
  }
};

const sortRows = (
  rows: WeeklyReportRow[],
  sortKey: SortKey,
  sortOrder: SortOrder
): WeeklyReportRow[] => {
  const direction = sortOrder === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aValue = getSortValue(a, sortKey);
    const bValue = getSortValue(b, sortKey);

    if (typeof aValue === "number" && typeof bValue === "number") {
      if (aValue === bValue) {
        return getPrimaryDateSortValue(a) - getPrimaryDateSortValue(b);
      }

      return (aValue - bValue) * direction;
    }

    const compared = String(aValue).localeCompare(String(bValue), undefined, {
      numeric: true,
      sensitivity: "base",
    });

    if (compared === 0) {
      return getPrimaryDateSortValue(a) - getPrimaryDateSortValue(b);
    }

    return compared * direction;
  });
};

const buildWeeklyTimelineRows = (
  report: WeeklyReportResponse | null,
  sortKey: SortKey,
  sortOrder: SortOrder
): WeeklyTimelineRow[] => {
  if (!report) {
    return [];
  }

  if (sortKey !== "date") {
    const sortedTerminalRows = sortRows(report.rows, sortKey, sortOrder);

    const terminalRows: WeeklyTimelineRow[] = sortedTerminalRows.map((row) => ({
      type: "terminal",
      key: `terminal-${row.id}`,
      timestamp: getTerminalTimelineTimestamp(row),
      row,
    }));

    const gasoilRows: WeeklyTimelineRow[] = [];

    report.gasoil?.vessels?.forEach((vesselSummary) => {
      (vesselSummary.measurements ?? []).forEach((measurement) => {
        if (!measurement.date) {
          return;
        }

        gasoilRows.push({
          type: "gasoil",
          key: `gasoil-${measurement.id}`,
          timestamp: getValidTimestamp(measurement.date),
          date: measurement.date,
          vessel: vesselSummary.vessel,
          measurement,
        });
      });

      (vesselSummary.bunkerings ?? []).forEach((bunkering) => {
        if (!bunkering.date) {
          return;
        }

        gasoilRows.push({
          type: "bunkering",
          key: `bunkering-${bunkering.id}`,
          timestamp: getValidTimestamp(bunkering.date),
          date: bunkering.date,
          vessel: vesselSummary.vessel,
          bunkering,
        });
      });
    });

    gasoilRows.sort((a, b) => a.timestamp - b.timestamp);

    return [...terminalRows, ...gasoilRows];
  }

  const timelineRows: WeeklyTimelineRow[] = [];

  report.rows.forEach((row) => {
    timelineRows.push({
      type: "terminal",
      key: `terminal-${row.id}`,
      timestamp: getTerminalTimelineTimestamp(row),
      row,
    });
  });

  report.gasoil?.vessels?.forEach((vesselSummary) => {
    (vesselSummary.measurements ?? []).forEach((measurement) => {
      if (!measurement.date) {
        return;
      }

      timelineRows.push({
        type: "gasoil",
        key: `gasoil-${measurement.id}`,
        timestamp: getValidTimestamp(measurement.date),
        date: measurement.date,
        vessel: vesselSummary.vessel,
        measurement,
      });
    });

    (vesselSummary.bunkerings ?? []).forEach((bunkering) => {
      if (!bunkering.date) {
        return;
      }

      timelineRows.push({
        type: "bunkering",
        key: `bunkering-${bunkering.id}`,
        timestamp: getValidTimestamp(bunkering.date),
        date: bunkering.date,
        vessel: vesselSummary.vessel,
        bunkering,
      });
    });
  });

  const direction = sortOrder === "asc" ? 1 : -1;

  const typeOrder: Record<WeeklyTimelineRow["type"], number> = {
    terminal: 0,
    gasoil: 1,
    bunkering: 2,
  };

  return timelineRows.sort((a, b) => {
    if (a.timestamp === b.timestamp) {
      return typeOrder[a.type] - typeOrder[b.type];
    }

    return (a.timestamp - b.timestamp) * direction;
  });
};

const renderDateTime = (
  value: ReportDateTime | null,
  locale: Locale
): React.ReactNode => {
  if (!value) {
    return (
      <Typography
        sx={{
          color: "text.secondary",
          fontWeight: 800,
          lineHeight: 1.2,
        }}
      >
        –
      </Typography>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: "0.86rem",
          lineHeight: 1.2,
          color: "text.primary",
        }}
      >
        {value.time}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,
          fontSize: "0.76rem",
          lineHeight: 1.2,
          color: "text.secondary",
          fontWeight: 600,
          textTransform: "capitalize",
        }}
      >
        {formatDisplayDateTimeDate(value, locale)}
      </Typography>
    </Box>
  );
};

const getStatusStyles = (status: ReportStatusKey) => {
  switch (status) {
    case "completed":
      return {
        color: "#15803d",
        backgroundColor: "#dcfce7",
        borderColor: "#bbf7d0",
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
      };

    case "ongoing":
      return {
        color: "#a16207",
        backgroundColor: "#fef3c7",
        borderColor: "#fde68a",
        icon: <AccessTimeIcon sx={{ fontSize: 16 }} />,
      };

    case "planned":
      return {
        color: "#1976d2",
        backgroundColor: "#e8f2ff",
        borderColor: "#cfe4ff",
        icon: <CalendarMonthIcon sx={{ fontSize: 16 }} />,
      };

    default:
      return {
        color: "#9333ea",
        backgroundColor: "#f3e8ff",
        borderColor: "#e9d5ff",
        icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
      };
  }
};

const getTranslatedStatusLabel = (
  status: ReportStatusKey,
  fallback: string,
  t: any
): string => {
  switch (status) {
    case "completed":
      return t("completed");

    case "ongoing":
      return t("ongoing");

    case "planned":
      return t("planned");

    case "to_plan":
      return t("toPlan");

    default:
      return fallback;
  }
};

const SummaryValueRow = ({
  label,
  value,
  valueColor = "#111827",
}: {
  label: string;
  value: string;
  valueColor?: string;
}) => {
  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 0.25,
      }}
    >
      <Typography
        noWrap
        sx={{
          minWidth: 0,
          color: "text.secondary",
          fontSize: "0.7rem",
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>

      <Typography
        noWrap
        sx={{
          flexShrink: 0,
          color: valueColor,
          fontSize: "0.78rem",
          fontWeight: 950,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const GasoilOverviewCard = ({
  gasoil,
  t,
}: {
  gasoil: WeeklyGasoilSummary;
  t: any;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 145,
        p: 1.75,
        borderRadius: WRAPPER_BORDER_RADIUS,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Box
        sx={{
          mb: 1.15,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: "#111827",
              fontSize: "0.9rem",
              lineHeight: 1.2,
              fontWeight: 950,
            }}
          >
            {t("gasoil", "Gasoil")}
          </Typography>

          <Typography
            noWrap
            sx={{
              mt: 0.15,
              color: "text.secondary",
              fontSize: "0.66rem",
              fontWeight: 650,
            }}
          >
            {t("weeklyConsumption", "Weekly consumption")}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: "9px",
            color: GASOIL_COLOR,
            backgroundColor: alpha(GASOIL_COLOR, 0.1),
          }}
        >
          <LocalGasStationOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "minmax(0, 1fr) minmax(155px, 0.72fr)",
          },
          gap: {
            xs: 1,
            sm: 1.75,
          },
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            pr: {
              sm: 1.75,
            },
            borderRight: {
              sm: "1px solid rgba(15, 23, 42, 0.08)",
            },
          }}
        >
          <SummaryValueRow
            label={t("startOfSelectedWeek", "Start of selected week")}
            value={formatGasoilAmount(gasoil.openingOnboard)}
            valueColor={GASOIL_COLOR}
          />

          <SummaryValueRow
            label={t("endOfSelectedWeek", "End of selected week")}
            value={formatGasoilAmount(gasoil.closingOnboard)}
            valueColor={GASOIL_COLOR}
          />

          <SummaryValueRow
            label={t("bunkeringsCount", {
              count: gasoil.bunkeringsCount,
              defaultValue:
                gasoil.bunkeringsCount === 1
                  ? "1 bunkering"
                  : `${gasoil.bunkeringsCount} bunkerings`,
            })}
            value={formatGasoilAmount(gasoil.bunkered)}
            valueColor={BUNKERING_COLOR}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pt: {
              xs: 0.8,
              sm: 0,
            },
            borderTop: {
              xs: "1px solid rgba(15, 23, 42, 0.08)",
              sm: "none",
            },
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.68rem",
              fontWeight: 750,
            }}
          >
            {t("totalGasoilConsumption", "Total gasoil consumption")}
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              color:
                gasoil.consumption === null
                  ? "text.secondary"
                  : "#dc2626",
              fontSize: {
                xs: "1.4rem",
                sm: "1.55rem",
              },
              lineHeight: 1.05,
              fontWeight: 950,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatGasoilAmount(gasoil.consumption)}
          </Typography>

          {gasoil.consumption === null ? (
            <Typography
              sx={{
                mt: 0.35,
                color: "text.secondary",
                fontSize: "0.62rem",
                lineHeight: 1.2,
                fontWeight: 650,
              }}
            >
              {t(
                "consumptionRequiresMeasurements",
                "Opening and closing measurements are required"
              )}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
};

const CargoOverviewCard = ({
  totals,
  terminalCallCount,
  t,
}: {
  totals: WeeklyReportTotals;
  terminalCallCount: number;
  t: any;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 145,
        p: 1.75,
        borderRadius: WRAPPER_BORDER_RADIUS,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Box
        sx={{
          mb: 1.15,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: "#111827",
              fontSize: "0.9rem",
              lineHeight: 1.2,
              fontWeight: 950,
            }}
          >
            {t("cargo", "Cargo")}
          </Typography>

          <Typography
            noWrap
            sx={{
              mt: 0.15,
              color: "text.secondary",
              fontSize: "0.66rem",
              fontWeight: 650,
            }}
          >
            {t(
              "weeklyCargoSummary",
              "Container activity for the selected week"
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: "9px",
            color: CARGO_COLOR,
            backgroundColor: alpha(CARGO_COLOR, 0.1),
          }}
        >
          <StackerViewIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "0.72fr 1fr 1fr",
          },
          gap: 1,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            px: 1.15,
            py: 0.9,
            borderRadius: "8px",
            backgroundColor: alpha(CARGO_COLOR, 0.055),
            border: `1px solid ${alpha(CARGO_COLOR, 0.12)}`,
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.65rem",
              lineHeight: 1.2,
              fontWeight: 700,
            }}
          >
            {t("terminalCallsThisWeek", "Terminal calls during this week")}
          </Typography>

          <Typography
            sx={{
              mt: 0.45,
              color: CARGO_COLOR,
              fontSize: "1.3rem",
              lineHeight: 1,
              fontWeight: 950,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {terminalCallCount}
          </Typography>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            px: 1.15,
            py: 0.9,
            borderRadius: "8px",
            backgroundColor: alpha(LOADING_COLOR, 0.055),
            border: `1px solid ${alpha(LOADING_COLOR, 0.12)}`,
          }}
        >
          <Typography
            sx={{
              color: LOADING_COLOR,
              fontSize: "0.68rem",
              fontWeight: 900,
            }}
          >
            {t("loading", "Loading")}
          </Typography>

          <Box
            sx={{
              mt: 0.4,
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: LOADING_COLOR,
                fontSize: "1.3rem",
                lineHeight: 1,
                fontWeight: 950,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totals.loadingContainers}
            </Typography>

            <Typography
              noWrap
              sx={{
                color: LOADING_COLOR,
                fontSize: "0.72rem",
                fontWeight: 850,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatGasoilNumber(totals.loadingTeu)} {t("teu")}
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 0.25,
              color: "text.secondary",
              fontSize: "0.61rem",
              fontWeight: 650,
            }}
          >
            {t("containers", "Containers")}
          </Typography>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            px: 1.15,
            py: 0.9,
            borderRadius: "8px",
            backgroundColor: alpha(UNLOADING_COLOR, 0.055),
            border: `1px solid ${alpha(UNLOADING_COLOR, 0.12)}`,
          }}
        >
          <Typography
            sx={{
              color: UNLOADING_COLOR,
              fontSize: "0.68rem",
              fontWeight: 900,
            }}
          >
            {t("unloading", "Unloading")}
          </Typography>

          <Box
            sx={{
              mt: 0.4,
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: UNLOADING_COLOR,
                fontSize: "1.3rem",
                lineHeight: 1,
                fontWeight: 950,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totals.unloadingContainers}
            </Typography>

            <Typography
              noWrap
              sx={{
                color: UNLOADING_COLOR,
                fontSize: "0.72rem",
                fontWeight: 850,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatGasoilNumber(totals.unloadingTeu)} {t("teu")}
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 0.25,
              color: "text.secondary",
              fontSize: "0.61rem",
              fontWeight: 650,
            }}
          >
            {t("containers", "Containers")}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export const YearOverviewDialog = ({
  open,
  loading,
  error,
  overview,
  locale,
  onAddMeasurement,
  onClose,
  t,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  overview: YearOverviewResponse | null;
  locale: Locale;
  onAddMeasurement?: (date: string) => void;
  onClose: () => void;
  t: any;
}) => {
  const weekDayLabels = useMemo(() => {
    const monday = startOfWeek(new Date(2024, 0, 1), {
      weekStartsOn: 1,
    });

    return Array.from({ length: 7 }, (_, index) =>
      format(addDays(monday, index), "EEE", {
        locale,
      })
    );
  }, [locale]);

  const todayIso = format(new Date(), "yyyy-MM-dd");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: {
            xs: "calc(100% - 16px)",
            sm: "calc(100% - 32px)",
            xl: "min(1600px, calc(100% - 48px))",
          },
          maxWidth: "1600px",
          height: {
            xs: "calc(100% - 16px)",
            sm: "min(900px, calc(100% - 48px))",
          },
          m: {
            xs: 1,
            sm: 2,
          },
          borderRadius: {
            xs: "10px",
            sm: "14px",
          },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 950 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950 }}>
            {t("yearOverview", "Year overview")} {overview?.year ?? ""}
          </Typography>

          <Typography
            sx={{
              mt: 0.2,
              color: "text.secondary",
              fontSize: "0.72rem",
              fontWeight: 650,
            }}
          >
            {t(
              "yearOverviewDescription",
              "Weekly gasoil measurements, bunkering and consumption for the current year."
            )}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: "0 !important",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 260,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={34} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer
              sx={{
                flex: 1,
                overflow: "auto",
                backgroundColor: "#fff",
              }}
            >
              <Table
                stickyHeader
                size="small"
                aria-label={t("yearOverview", "Year overview")}
                sx={{
                  minWidth: 1420,
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": {
                    borderColor: "rgba(15, 23, 42, 0.09)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        left: 0,
                        zIndex: 4,
                        width: 116,
                        backgroundColor: "#f8fbff",
                        fontWeight: 950,
                      }}
                    >
                      {t("week")}
                    </TableCell>

                    {weekDayLabels.map((label) => (
                      <TableCell
                        key={label}
                        align="center"
                        sx={{
                          width: 118,
                          backgroundColor: "#f8fbff",
                          fontWeight: 950,
                          textTransform: "capitalize",
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}

                    {[
                      t("openingOnboard", "Opening onboard"),
                      t("closingOnboard", "Closing onboard"),
                      t("bunkered", "Bunkered"),
                      t("consumed", "Consumed"),
                    ].map((label) => (
                      <TableCell
                        key={label}
                        align="center"
                        sx={{
                          width: 118,
                          backgroundColor: alpha(GASOIL_COLOR, 0.025),
                          fontWeight: 950,
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {overview?.weeks.length ? (
                    overview.weeks.slice().reverse().map((week) => {
                      const isCurrentWeek =
                        todayIso >= week.weekStart &&
                        todayIso <= week.weekEnd;

                      return (
                        <TableRow
                          key={`${week.weekYear}-${week.week}`}
                          sx={{
                            backgroundColor: isCurrentWeek
                              ? alpha("#1976d2", 0.025)
                              : "#fff",
                            "& > td": {
                              height: 112,
                              verticalAlign: "middle",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                              backgroundColor: isCurrentWeek
                                ? "#f2f7fd"
                                : "#fff",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#111827",
                                fontSize: "1rem",
                                fontWeight: 950,
                              }}
                            >
                              W{week.week}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.45,
                                color: "text.secondary",
                                fontSize: "0.66rem",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDisplayDate(week.weekStart, locale)}
                              {" – "}
                              {formatDisplayDate(week.weekEnd, locale)}
                            </Typography>
                          </TableCell>

                          {week.days.map((day, dayIdx) => {
                            const measurementIndex =
                              dayIdx === 0
                                ? 0
                                : day.measurements.length - 1;
                            const latestMeasurement =
                              day.measurements[measurementIndex] ?? null;

                            return (
                              <TableCell
                                key={day.date}
                                align="center"
                                sx={{
                                  p: 0.75,
                                  backgroundColor:
                                    day.date === todayIso
                                      ? alpha("#1976d2", 0.04)
                                      : "inherit",
                                }}
                              >
                                {latestMeasurement ? (
                                  <Box
                                    sx={{
                                      position: "relative",
                                      minHeight: 72,
                                      px: 0.75,
                                      py: 1,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: `1px solid ${alpha(
                                        GASOIL_COLOR,
                                        0.15
                                      )}`,
                                      borderRadius: "9px",
                                      backgroundColor: alpha(
                                        GASOIL_COLOR,
                                        0.035
                                      ),
                                    }}
                                  >
                                    {day.measurements.length > 1 ? (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: -8,
                                          right: -6,
                                          width: 22,
                                          height: 22,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          borderRadius: "50%",
                                          color: "#fff",
                                          backgroundColor: GASOIL_COLOR,
                                          fontSize: "0.65rem",
                                          fontWeight: 950,
                                          boxShadow:
                                            "0 2px 5px rgba(15, 23, 42, 0.2)",
                                        }}
                                      >
                                        {day.measurements.length}
                                      </Box>
                                    ) : null}

                                    <Typography
                                      sx={{
                                        color: GASOIL_COLOR,
                                        fontSize: "0.83rem",
                                        fontWeight: 950,
                                        fontVariantNumeric: "tabular-nums",
                                      }}
                                    >
                                      {formatGasoilAmount(
                                        latestMeasurement.amount
                                      )}
                                    </Typography>

                                    <Typography
                                      sx={{
                                        mt: 0.4,
                                        color: "text.secondary",
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {formatTimelineTime(
                                        latestMeasurement.date
                                      )}
                                    </Typography>

                                    {day.bunkerings.length ? (
                                      <Typography
                                        sx={{
                                          mt: 0.45,
                                          color: BUNKERING_COLOR,
                                          fontSize: "0.62rem",
                                          fontWeight: 900,
                                        }}
                                      >
                                        +{formatGasoilAmount(
                                          day.bunkerings.reduce(
                                            (total, item) =>
                                              total + Number(item.amount),
                                            0
                                          )
                                        )}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ) : day.bunkerings.length ? (
                                  <Box
                                    sx={{
                                      position: "relative",
                                      minHeight: 72,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: BUNKERING_COLOR,
                                    }}
                                  >
                                    {onAddMeasurement ? (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          onAddMeasurement(day.date)
                                        }
                                        aria-label={t(
                                          "addMeasurementForDate",
                                          {
                                            date: day.date,
                                            defaultValue: `Add measurement for ${day.date}`,
                                          }
                                        )}
                                        sx={{
                                          position: "absolute",
                                          top: -4,
                                          right: -2,
                                          width: 24,
                                          height: 24,
                                          border: `1px dashed ${alpha(
                                            GASOIL_COLOR,
                                            0.2
                                          )}`,
                                          color: alpha(
                                            GASOIL_COLOR,
                                            0.62
                                          ),
                                          backgroundColor: "#fff",
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        +
                                      </IconButton>
                                    ) : null}

                                    <LocalGasStationOutlinedIcon
                                      sx={{ fontSize: 18 }}
                                    />
                                    <Typography
                                      sx={{
                                        mt: 0.35,
                                        fontSize: "0.68rem",
                                        fontWeight: 900,
                                      }}
                                    >
                                      +
                                      {formatGasoilAmount(
                                        day.bunkerings.reduce(
                                          (total, item) =>
                                            total + Number(item.amount),
                                          0
                                        )
                                      )}
                                    </Typography>
                                  </Box>
                                ) : onAddMeasurement ? (
                                  <Button
                                    onClick={() =>
                                      onAddMeasurement(day.date)
                                    }
                                    aria-label={t(
                                      "addMeasurementForDate",
                                      {
                                        date: day.date,
                                        defaultValue: `Add measurement for ${day.date}`,
                                      }
                                    )}
                                    sx={{
                                      width: "100%",
                                      minWidth: 0,
                                      minHeight: 72,
                                      border: `1px dashed ${alpha(
                                        GASOIL_COLOR,
                                        0.17
                                      )}`,
                                      borderRadius: "9px",
                                      color: alpha(GASOIL_COLOR, 0.58),
                                      backgroundColor: "#fff",
                                      fontSize: "1.25rem",
                                      fontWeight: 500,
                                      "&:hover": {
                                        borderColor: alpha(
                                          GASOIL_COLOR,
                                          0.42
                                        ),
                                        backgroundColor: alpha(
                                          GASOIL_COLOR,
                                          0.035
                                        ),
                                      },
                                    }}
                                  >
                                    +
                                  </Button>
                                ) : (
                                  <Typography
                                    sx={{
                                      color: "text.disabled",
                                      fontSize: "1rem",
                                    }}
                                  >
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}

                          <TableCell align="center" sx={{ backgroundColor: alpha(GASOIL_COLOR, 0.015) }}>
                            <Typography
                              sx={{
                                color: GASOIL_COLOR,
                                fontWeight: 950,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatGasoilAmount(
                                week.openingOnboard?.amount
                              )}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                color: "text.secondary",
                                fontSize: "0.65rem",
                                fontWeight: 700,
                              }}
                            >
                              {formatTimelineTime(
                                week.openingOnboard?.date
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell align="center" sx={{ backgroundColor: alpha(GASOIL_COLOR, 0.015) }}>
                            <Typography
                              sx={{
                                color: GASOIL_COLOR,
                                fontWeight: 950,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatGasoilAmount(
                                week.closingOnboard?.amount
                              )}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                color: "text.secondary",
                                fontSize: "0.65rem",
                                fontWeight: 700,
                              }}
                            >
                              {formatTimelineTime(
                                week.closingOnboard?.date
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell align="center" sx={{ backgroundColor: alpha(GASOIL_COLOR, 0.015) }}>
                            <Typography
                              sx={{
                                color: BUNKERING_COLOR,
                                fontWeight: 950,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {week.bunkered
                                ? `+${formatGasoilAmount(week.bunkered)}`
                                : formatGasoilAmount(0)}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                color: "text.secondary",
                                fontSize: "0.65rem",
                                fontWeight: 700,
                              }}
                            >
                              {t("bunkeringsCount", {
                                count: week.bunkeringsCount,
                                defaultValue: `${week.bunkeringsCount} bunkerings`,
                              })}
                            </Typography>
                          </TableCell>

                          <TableCell align="center" sx={{ backgroundColor: alpha(GASOIL_COLOR, 0.015) }}>
                            <Typography
                              sx={{
                                color:
                                  week.consumption === null
                                    ? "text.disabled"
                                    : "#dc2626",
                                fontWeight: 950,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatGasoilAmount(week.consumption)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Box sx={{ py: 6 }}>
                          <Typography
                            sx={{
                              color: "text.secondary",
                              fontWeight: 800,
                            }}
                          >
                            {t(
                              "noYearOverviewData",
                              "No yearly gasoil data is available."
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                p: {
                  xs: 1.25,
                  sm: 2,
                },
                display: "flex",
                flexWrap: "wrap",
                gap: {
                  xs: 1.5,
                  sm: 3,
                },
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    border: `1px solid ${alpha(GASOIL_COLOR, 0.15)}`,
                    borderRadius: "5px",
                    backgroundColor: alpha(GASOIL_COLOR, 0.05),
                  }}
                />
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                  {t("measurementAvailable", "Measurement available")}
                </Typography>
              </Box>

              {onAddMeasurement ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px dashed ${alpha(GASOIL_COLOR, 0.2)}`,
                      borderRadius: "6px",
                      color: alpha(GASOIL_COLOR, 0.58),
                      fontSize: "1rem",
                    }}
                  >
                    +
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                    {t("addMeasurement", "Add measurement")}
                  </Typography>
                </Box>
              ) : null}

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    color: "#fff",
                    backgroundColor: GASOIL_COLOR,
                    fontSize: "0.65rem",
                    fontWeight: 950,
                  }}
                >
                  2
                </Box>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                  {t("multipleMeasurements", "Multiple measurements")}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <LocalGasStationOutlinedIcon
                  sx={{ color: BUNKERING_COLOR, fontSize: 18 }}
                />
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                  {t("bunkeringEvent", "Bunkering event")}
                </Typography>
              </Box>

            </Box>

            <Box
              sx={{
                mx: {
                  xs: 1.25,
                  sm: 2,
                },
                mb: {
                  xs: 1.25,
                  sm: 2,
                },
                px: 1.5,
                py: 1.25,
                border: `1px solid ${alpha("#1976d2", 0.25)}`,
                borderRadius: "9px",
                backgroundColor: alpha("#1976d2", 0.025),
              }}
            >
              <Typography
                sx={{
                  color: GASOIL_COLOR,
                  fontSize: "0.75rem",
                  fontWeight: 900,
                }}
              >
                {t("aboutConsumption", "About consumption")}
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: "text.secondary",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                {t(
                  "consumptionFormula",
                  "Consumption = opening onboard + bunkered − closing onboard"
                )}
              </Typography>

              <Typography
                sx={{
                  mt: 0.2,
                  color: "text.secondary",
                  fontSize: "0.68rem",
                  fontWeight: 650,
                }}
              >
                {t("allValuesInLiters", "All values are in litres (L).")}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WeeklySummarySection = ({
  gasoil,
  totals,
  terminalCallCount,
  locale,
  onOpenYearOverview,
  t,
}: {
  gasoil: WeeklyGasoilSummary;
  totals: WeeklyReportTotals;
  terminalCallCount: number;
  locale: Locale;
  onOpenYearOverview: () => void;
  t: any;
}) => {
  const hasAnyGasoilData =
    gasoil.openingOnboard !== null ||
    gasoil.closingOnboard !== null ||
    gasoil.bunkered > 0 ||
    gasoil.measurementsCount > 0 ||
    gasoil.bunkeringsCount > 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 950,
              color: "#111827",
            }}
          >
            {t("weeklySummary", "Weekly summary")}
          </Typography>

          <Typography
            sx={{
              mt: 0.15,
              fontSize: "0.7rem",
              fontWeight: 650,
              color: "text.secondary",
            }}
          >
            {t(
              "weeklySummaryDescription",
              "Gasoil consumption and cargo activity for the selected week."
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {hasAnyGasoilData && !gasoil.hasCompleteConsumptionData ? (
            <Chip
              size="small"
              color="warning"
              label={t(
                "incompleteConsumptionData",
                "Incomplete measurement data"
              )}
              sx={{
                height: 26,
                fontSize: "0.68rem",
                fontWeight: 850,
              }}
            />
          ) : null}

          <Button
            size="small"
            variant="outlined"
            onClick={onOpenYearOverview}
            startIcon={<CalendarViewMonthOutlinedIcon />}
            sx={{
              minHeight: 34,
              px: 1.5,
              borderRadius: WRAPPER_BORDER_RADIUS,
              textTransform: "none",
              fontSize: "0.74rem",
              fontWeight: 850,
              backgroundColor: "#fff",
            }}
          >
            {t("yearOverview", "Year overview")}
          </Button>
        </Box>
      </Box>

      {!hasAnyGasoilData ? (
        <Alert
          severity="info"
          sx={{
            mb: 1.25,
            py: 0.25,
            borderRadius: WRAPPER_BORDER_RADIUS,
          }}
        >
          {t(
            "noWeeklyGasoilData",
            "No gasoil measurements or bunkerings were found for this report."
          )}
        </Alert>
      ) : null}

      <Grid container spacing={1.25}>
        <Grid item xs={12} lg={6}>
          <GasoilOverviewCard gasoil={gasoil} t={t} />
        </Grid>

        <Grid item xs={12} lg={6}>
          <CargoOverviewCard
            totals={totals}
            terminalCallCount={terminalCallCount}
            t={t}
          />
        </Grid>
      </Grid>

      {gasoil.vessels.length > 1 ? (
        <TableContainer
          component={Paper}
          sx={{
            mt: 1.25,
            borderRadius: WRAPPER_BORDER_RADIUS,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "none",
            overflowX: "auto",
          }}
        >
          <Table
            size="small"
            sx={{
              minWidth: 760,
              "& .MuiTableCell-root": {
                borderColor: "rgba(15, 23, 42, 0.08)",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#f8fbff",
                    fontWeight: 950,
                  }}
                >
                  {t("vessel")}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: "#f8fbff",
                    fontWeight: 950,
                  }}
                >
                  {t("openingOnboard", "Opening onboard")}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: "#f8fbff",
                    fontWeight: 950,
                  }}
                >
                  {t("bunkered", "Bunkered")}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: "#f8fbff",
                    fontWeight: 950,
                  }}
                >
                  {t("closingOnboard", "Closing onboard")}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: "#f8fbff",
                    fontWeight: 950,
                  }}
                >
                  {t("consumed", "Consumed")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {gasoil.vessels.map((item) => (
                <TableRow key={item.vessel.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 850 }}>
                      {item.vessel.name || t("unknown")}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "0.68rem",
                        color: "text.secondary",
                      }}
                    >
                      {t("measurementsCount", {
                        count: item.measurementsCount,
                        defaultValue: `${item.measurementsCount} measurements`,
                      })}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 750 }}>
                      {formatGasoilAmount(item.openingOnboard?.amount)}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: "0.68rem",
                        color: "text.secondary",
                      }}
                    >
                      {formatGasoilMeasurementDate(
                        item.openingOnboard?.date,
                        locale
                      )}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography
                      sx={{
                        fontWeight: 850,
                        color: BUNKERING_COLOR,
                      }}
                    >
                      {formatGasoilAmount(item.bunkered)}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: "0.68rem",
                        color: "text.secondary",
                      }}
                    >
                      {t("bunkeringsCount", {
                        count: item.bunkeringsCount,
                        defaultValue: `${item.bunkeringsCount} bunkerings`,
                      })}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 750 }}>
                      {formatGasoilAmount(item.closingOnboard?.amount)}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: "0.68rem",
                        color: "text.secondary",
                      }}
                    >
                      {formatGasoilMeasurementDate(
                        item.closingOnboard?.date,
                        locale
                      )}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color:
                          item.consumption === null
                            ? "text.secondary"
                            : "#dc2626",
                      }}
                    >
                      {formatGasoilAmount(item.consumption)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
};

const exportToCsv = (
  report: WeeklyReportResponse,
  t: any,
  locale: Locale,
  rowsOverride?: WeeklyReportRow[]
) => {
  const headers = [
    t("date"),
    t("day"),
    t("terminal"),
    t("location"),
    t("planned"),
    t("arrival"),
    t("operationStart"),
    t("operationEnd"),
    t("departure"),
    t("loadingContainers"),
    t("loadingTeu"),
    t("unloadingContainers"),
    t("unloadingTeu"),
    t("status"),
  ];

  const rowsToExport = rowsOverride ?? report.rows;

  const rows: Array<Array<string | number>> = rowsToExport.map((row) => [
    formatCsvDate(row.dateIso),
    formatCsvDay(row.dateIso, row.day, locale),
    row.terminal.name ?? "",
    row.terminal.city ?? "",
    formatCsvDateTime(row.planned),
    formatCsvDateTime(row.arrival),
    formatCsvDateTime(row.operationStart),
    formatCsvDateTime(row.operationEnd),
    formatCsvDateTime(row.departure),
    row.loading.containers,
    row.loading.teu,
    row.unloading.containers,
    row.unloading.teu,
    getTranslatedStatusLabel(row.status.key, row.status.label, t),
  ]);

  rows.push([
    t("weeklyTotal"),
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    report.totals.loadingContainers,
    report.totals.loadingTeu,
    report.totals.unloadingContainers,
    report.totals.unloadingTeu,
    "",
  ]);

  const gasoilRows: Array<Array<string | number>> = [
    [],
    [t("weeklyGasoilSummary", "Weekly gasoil summary")],
    [
      t("openingOnboard", "Opening onboard"),
      report.gasoil?.openingOnboard ?? "",
    ],
    [
      t("totalBunkered", "Total bunkered"),
      report.gasoil?.bunkered ?? 0,
    ],
    [
      t("closingOnboard", "Closing onboard"),
      report.gasoil?.closingOnboard ?? "",
    ],
    [
      t("totalGasoilConsumption", "Total gasoil consumption"),
      report.gasoil?.consumption ?? "",
    ],
    [],
    [
      t("type", "Type"),
      t("vessel"),
      t("dateTime", "Date and time"),
      t("amountLitersShort", "Amount (L)"),
      t("supplier", "Supplier"),
    ],
  ];

  (report.gasoil?.vessels ?? []).forEach((vesselSummary) => {
    (vesselSummary.measurements ?? []).forEach((measurement) => {
      gasoilRows.push([
        t("gasoilMeasurement", "Gasoil measurement"),
        vesselSummary.vessel.name ?? "",
        formatCsvIsoDateTime(measurement.date),
        measurement.amount,
        "",
      ]);
    });

    (vesselSummary.bunkerings ?? []).forEach((bunkering) => {
      gasoilRows.push([
        t("bunkering", "Bunkering"),
        vesselSummary.vessel.name ?? "",
        formatCsvIsoDateTime(bunkering.date),
        bunkering.amount,
        bunkering.supplier ?? "",
      ]);
    });
  });

  const csv = [headers, ...rows, ...gasoilRows]
    .map((row) =>
      row
        .map((cell) => {
          const value = String(cell ?? "");

          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(";")
    )
    .join("\n");

  const blob = new Blob([`\uFEFF${csv}`], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `weekly-operations-${report.weekStart}-${report.weekEnd}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

const WeeklyOperationsReport = () => {
  const { t, i18n } = useTranslation();

  const dateLocale = useMemo(() => {
    return getDateFnsLocale(i18n.language);
  }, [i18n.language]);

  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), {
      weekStartsOn: 1,
    })
  );

  const [report, setReport] = useState<WeeklyReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [showGasoilDetails, setShowGasoilDetails] = useState(true);

  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [weekPickerAnchor, setWeekPickerAnchor] =
    useState<HTMLElement | null>(null);
  const [yearOverviewOpen, setYearOverviewOpen] = useState(false);

  const title = useMemo(() => {
    if (
      report?.vesselName &&
      report.vesselName !== "All vessels" &&
      report.vesselName !== "Alle schepen"
    ) {
      return t("weeklyOperationsReportForVessel", {
        vessel: report.vesselName,
      });
    }

    return t("weeklyOperationsReport");
  }, [report?.vesselName, t]);

  const weekNumber = useMemo(() => getWeek(weekStart, { weekStartsOn: 1 }), [weekStart]);

  const sortedTerminalRows = useMemo(() => {
    return sortRows(report?.rows ?? [], sortKey, sortOrder);
  }, [report?.rows, sortKey, sortOrder]);

  const timelineRows = useMemo(() => {
    return buildWeeklyTimelineRows(report, sortKey, sortOrder);
  }, [report, sortKey, sortOrder]);

  const visibleTimelineRows = useMemo(() => {
    if (showGasoilDetails) {
      return timelineRows;
    }

    return timelineRows.filter(
      (timelineRow) => timelineRow.type === "terminal"
    );
  }, [timelineRows, showGasoilDetails]);

  const yearOverviewVesselId = useMemo(() => {
    const vessels = report?.gasoil?.vessels ?? [];

    return vessels.length === 1 ? vessels[0].vessel.id : null;
  }, [report?.gasoil?.vessels]);

  const handleRequestSort = (nextSortKey: SortKey) => {
    if (sortKey === nextSortKey) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(nextSortKey);
    setSortOrder("asc");
  };

  const renderSortLabel = (
    label: React.ReactNode,
    nextSortKey: SortKey,
    align: "left" | "center" = "left"
  ) => {
    return (
      <TableSortLabel
        active={sortKey === nextSortKey}
        direction={sortKey === nextSortKey ? sortOrder : "asc"}
        onClick={() => handleRequestSort(nextSortKey)}
        sx={{
          width: align === "center" ? "100%" : "auto",
          justifyContent: align === "center" ? "center" : "flex-start",
          color: "#111827 !important",
          "& .MuiTableSortLabel-icon": {
            color: "#111827 !important",
          },
        }}
      >
        {label}
      </TableSortLabel>
    );
  };

  const fetchWeeklyReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      params.set("weekStart", format(weekStart, "yyyy-MM-dd"));
      params.set("weekEnd", format(endOfWeek(weekStart, { weekStartsOn: 1 }), "yyyy-MM-dd"));

      const response = await window.fetch(
        `${getApiBaseUrl()}/reports/weekly-operations?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/ld+json; charset=utf-8",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        let responseMessage = "";

        try {
          const errorData = await response.json();

          responseMessage =
            errorData?.["hydra:description"] ||
            errorData?.detail ||
            errorData?.message ||
            "";
        } catch {
          responseMessage = "";
        }

        throw new Error(
          responseMessage ||
            response.statusText ||
            t("failedToFetchWeeklyReport")
        );
      }

      const data = (await response.json()) as WeeklyReportResponse;

      setReport(data);
    } catch (err: any) {
      setError(err?.message || t("failedToFetchWeeklyReport"));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [weekStart, t]);

  useEffect(() => {
    void fetchWeeklyReport();
  }, [fetchWeeklyReport]);

  const handlePreviousWeek = () => {
    setWeekStart((current) => subWeeks(current, 1));
  };

  const handleNextWeek = () => {
    setWeekStart((current) => addWeeks(current, 1));
  };

  const handleOpenWeekPicker = (event: React.MouseEvent<HTMLElement>) => {
    setWeekPickerAnchor(event.currentTarget);
  };

  const handleCloseWeekPicker = () => {
    setWeekPickerAnchor(null);
  };

  const handleWeekDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    if (!value) {
      return;
    }

    const selectedDate = new Date(`${value}T00:00:00`);

    if (Number.isNaN(selectedDate.getTime())) {
      return;
    }

    setWeekStart(
      startOfWeek(selectedDate, {
        weekStartsOn: 1,
      })
    );

    handleCloseWeekPicker();
  };

  const handleOpenExport = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchor(event.currentTarget);
  };

  const handleCloseExport = () => {
    setExportAnchor(null);
  };

  const handleExportCsv = () => {
    if (report) {
      exportToCsv(report, t, dateLocale, sortedTerminalRows);
    }

    handleCloseExport();
  };

  const handleOpenYearOverview = () => {
    setYearOverviewOpen(true);
  };

  return (
    <Layout>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 100%)",
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{
            px: 2,
            py: 2,
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {/* <Box
              sx={(theme) => ({
                width: 52,
                height: 52,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                boxShadow: `0 8px 18px ${alpha(
                  theme.palette.primary.main,
                  0.12
                )}`,
              })}
            >
              <CalendarMonthIcon />
            </Box>
 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "2.8rem",
                  fontWeight: 950,
                  lineHeight: 1,
                  color: alpha("#18316f", 0.6),
                  letterSpacing: "-0.03em",
                  userSelect: "none",
                }}
              >
                W{weekNumber}
              </Typography>

              <Box>
                <Typography
                  sx={{
                    fontSize: "1.45rem",
                    fontWeight: 950,
                    lineHeight: 1.15,
                    color: "#0f172a",
                  }}
                >
                  {title}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  {t("weeklyOperationsReportDescription")}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box
              component={Paper}
              sx={{
                height: 44,
                display: "flex",
                alignItems: "center",
                borderRadius: WRAPPER_BORDER_RADIUS,
                overflow: "hidden",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                boxShadow: "0 3px 10px rgba(15, 23, 42, 0.05)",
              }}
            >
              <IconButton
                onClick={handlePreviousWeek}
                title={t("previousWeek")}
                aria-label={t("previousWeek")}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 0,
                  borderRight: "1px solid rgba(15, 23, 42, 0.08)",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Button
                onClick={handleOpenWeekPicker}
                sx={{
                  px: 2,
                  height: 44,
                  minWidth: 190,
                  borderRadius: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  color: "#1f2937",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(15, 23, 42, 0.03)",
                  },
                }}
              >
                <CalendarMonthIcon
                  sx={{
                    fontSize: 18,
                    color: "text.secondary",
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.9rem",
                    color: "#1f2937",
                    textTransform: "capitalize",
                  }}
                >
                  {`W${weekNumber}: ${formatWeekRange(weekStart, dateLocale)}`}
                </Typography>
              </Button>

              <IconButton
                onClick={handleNextWeek}
                title={t("nextWeek")}
                aria-label={t("nextWeek")}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 0,
                  borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Menu
              anchorEl={weekPickerAnchor}
              open={Boolean(weekPickerAnchor)}
              onClose={handleCloseWeekPicker}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  p: 1.5,
                  borderRadius: WRAPPER_BORDER_RADIUS,
                  minWidth: 260,
                },
              }}
            >
              <Box sx={{ px: 1, pb: 1 }}>
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "0.8rem",
                    fontWeight: 850,
                    color: "#111827",
                  }}
                >
                  {t("chooseDateInWeek")}
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={format(weekStart, "yyyy-MM-dd")}
                  onChange={handleWeekDateChange}
                  inputProps={{
                    max: "9999-12-31",
                  }}
                />
              </Box>
            </Menu>

            <Button
              variant="outlined"
              onClick={handleOpenExport}
              startIcon={<DownloadIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              disabled={!report}
              sx={{
                height: 44,
                px: 2,
                textTransform: "none",
                fontWeight: 850,
                borderRadius: WRAPPER_BORDER_RADIUS,
                borderColor: "rgba(15, 23, 42, 0.12)",
                color: "#111827",
                backgroundColor: "#ffffff",
                boxShadow: "0 3px 10px rgba(15, 23, 42, 0.05)",
              }}
            >
              {t("export")}
            </Button>

            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={handleCloseExport}
            >
              <MenuItem disabled={!report} onClick={handleExportCsv}>
                {t("exportCsv")}
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 2,
            position: "relative",
          }}
        >
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {report?.gasoil ? (
            <WeeklySummarySection
              gasoil={report.gasoil}
              totals={report.totals}
              terminalCallCount={report.rows.length}
              locale={dateLocale}
              onOpenYearOverview={handleOpenYearOverview}
              t={t}
            />
          ) : null}

          <Box
            sx={{
              mb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              variant={showGasoilDetails ? "contained" : "outlined"}
              onClick={() => {
                setShowGasoilDetails((current) => !current);
              }}
              startIcon={
                showGasoilDetails ? (
                  <VisibilityOffOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )
              }
              sx={{
                minHeight: 34,
                px: 1.5,
                borderRadius: WRAPPER_BORDER_RADIUS,
                textTransform: "none",
                fontSize: "0.74rem",
                fontWeight: 850,
                boxShadow: showGasoilDetails
                  ? "0 3px 10px rgba(25, 118, 210, 0.16)"
                  : "none",
              }}
            >
              {showGasoilDetails
                ? t(
                    "hideGasoilAndBunkeringDetails",
                    "Hide gasoil and bunkering details"
                  )
                : t(
                    "showGasoilAndBunkeringDetails",
                    "Show gasoil and bunkering details"
                  )}
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: WRAPPER_BORDER_RADIUS,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              overflow: "hidden",
              position: "relative",
              minHeight: loading && !report ? 300 : undefined,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.72)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <CircularProgress size={30} />
              </Box>
            ) : null}

            <Table
              stickyHeader
              sx={{
                minWidth: 1350,
                "& .MuiTableCell-root": {
                  borderColor: "rgba(15, 23, 42, 0.08)",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sortDirection={sortKey === "date" ? sortOrder : false}
                    sx={{
                      width: 120,
                      backgroundColor: "#f8fbff",
                      fontWeight: 950,
                      color: "#111827",
                    }}
                  >
                    <Box>
                      {renderSortLabel(
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 950,
                          }}
                        >
                          {t("date")}
                        </Typography>,
                        "date"
                      )}

                      <Typography
                        sx={{
                          mt: 0.45,
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          fontWeight: 700,
                        }}
                      >
                        {t("day")}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell
                    rowSpan={2}
                    sortDirection={sortKey === "terminal" ? sortOrder : false}
                    sx={{
                      width: 235,
                      backgroundColor: "#f8fbff",
                      fontWeight: 950,
                      color: "#111827",
                    }}
                  >
                    <Box>
                      {renderSortLabel(
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 950,
                          }}
                        >
                          {t("terminal")}
                        </Typography>,
                        "terminal"
                      )}

                      <Typography
                        sx={{
                          mt: 0.45,
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          fontWeight: 700,
                        }}
                      >
                        {t("location")}
                      </Typography>
                    </Box>
                  </TableCell>

                  {[
                    {
                      label: t("planned"),
                      sortKey: "planned" as SortKey,
                    },
                    {
                      label: t("arrival"),
                      sortKey: "arrival" as SortKey,
                    },
                    {
                      label: t("operationStart"),
                      sortKey: "operationStart" as SortKey,
                    },
                    {
                      label: t("operationEnd"),
                      sortKey: "operationEnd" as SortKey,
                    },
                    {
                      label: t("departure"),
                      sortKey: "departure" as SortKey,
                    },
                  ].map((column) => (
                    <TableCell
                      key={column.sortKey}
                      rowSpan={2}
                      align="center"
                      sortDirection={
                        sortKey === column.sortKey ? sortOrder : false
                      }
                      sx={{
                        width: 135,
                        backgroundColor: "#f8fbff",
                        fontWeight: 950,
                        color: "#111827",
                      }}
                    >
                      {renderSortLabel(
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                          }}
                        >
                          <AccessTimeIcon
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                            }}
                          />

                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 950,
                            }}
                          >
                            {column.label}
                          </Typography>
                        </Box>,
                        column.sortKey,
                        "center"
                      )}
                    </TableCell>
                  ))}

                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{
                      backgroundColor: "#f8fbff",
                      fontWeight: 950,
                      color: "#111827",
                    }}
                  >
                    {t("loading")}
                  </TableCell>

                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{
                      backgroundColor: "#f8fbff",
                      fontWeight: 950,
                      color: "#111827",
                    }}
                  >
                    {t("unloading")}
                  </TableCell>

                  <TableCell
                    rowSpan={2}
                    align="center"
                    sortDirection={sortKey === "status" ? sortOrder : false}
                    sx={{
                      width: 160,
                      backgroundColor: "#f8fbff",
                      fontWeight: 950,
                      color: "#111827",
                    }}
                  >
                    {renderSortLabel(t("status"), "status", "center")}
                  </TableCell>
                </TableRow>

                <TableRow>
                  {[
                    {
                      key: "loadingContainers",
                      label: t("containers"),
                    },
                    {
                      key: "loadingTeu",
                      label: t("teu"),
                    },
                    {
                      key: "unloadingContainers",
                      label: t("containers"),
                    },
                    {
                      key: "unloadingTeu",
                      label: t("teu"),
                    },
                  ].map((column) => (
                    <TableCell
                      key={column.key}
                      align="center"
                      sortDirection={sortKey === column.key ? sortOrder : false}
                      sx={{
                        width: 95,
                        backgroundColor: "#f8fbff",
                        fontSize: "0.72rem",
                        fontWeight: 950,
                        color: "#111827",
                      }}
                    >
                      {renderSortLabel(
                        column.label,
                        column.key as SortKey,
                        "center"
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleTimelineRows.length ? (
                  visibleTimelineRows.map((timelineRow) => {
                    if (timelineRow.type === "gasoil") {
                      const measurement = timelineRow.measurement;

                      return (
                        <TableRow
                          key={timelineRow.key}
                          sx={{
                            backgroundColor: alpha(GASOIL_COLOR, 0.045),
                            "&:hover td": {
                              backgroundColor: alpha(GASOIL_COLOR, 0.08),
                            },
                            "& td": {
                              borderColor: alpha(GASOIL_COLOR, 0.12),
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 850,
                                color: "#111827",
                                fontSize: "0.83rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {formatTimelineDate(
                                measurement.date,
                                dateLocale
                              )}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.35,
                                color: "text.secondary",
                                fontSize: "0.74rem",
                                fontWeight: 650,
                                textTransform: "capitalize",
                              }}
                            >
                              {formatTimelineDay(
                                measurement.date,
                                dateLocale
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: GASOIL_COLOR,
                                  fontSize: "0.83rem",
                                }}
                              >
                                {t(
                                  "gasoilMeasurement",
                                  "Gasoil measurement"
                                )}
                              </Typography>

                              <Typography
                                noWrap
                                sx={{
                                  mt: 0.25,
                                  color: "text.secondary",
                                  fontSize: "0.72rem",
                                  fontWeight: 650,
                                }}
                              >
                                {timelineRow.vessel.name ||
                                  t("unknownVessel", "Unknown vessel")}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell colSpan={5} align="center">
                            <Box
                              sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: GASOIL_COLOR,
                                  fontSize: "1rem",
                                  lineHeight: 1.2,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatGasoilAmount(measurement.amount)}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.35,
                                  color: "text.secondary",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                }}
                              >
                                {formatTimelineTime(measurement.date)}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell colSpan={4} align="center">
                            <Typography
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                              }}
                            >
                              {measurement.updatedAt
                                ? t("updatedAtDate", {
                                    date: formatGasoilMeasurementDate(
                                      measurement.updatedAt,
                                      dateLocale
                                    ),
                                    defaultValue: `Updated ${formatGasoilMeasurementDate(
                                      measurement.updatedAt,
                                      dateLocale
                                    )}`,
                                  })
                                : "—"}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={<OpacityOutlinedIcon />}
                              label={t("gasoil", "Gasoil")}
                              sx={{
                                height: 28,
                                borderRadius: "8px",
                                border: `1px solid ${alpha(
                                  GASOIL_COLOR,
                                  0.2
                                )}`,
                                backgroundColor: alpha(GASOIL_COLOR, 0.1),
                                color: GASOIL_COLOR,
                                fontWeight: 950,
                                fontSize: "0.68rem",
                                "& .MuiChip-icon": {
                                  color: GASOIL_COLOR,
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    }

                    if (timelineRow.type === "bunkering") {
                      const bunkering = timelineRow.bunkering;

                      return (
                        <TableRow
                          key={timelineRow.key}
                          sx={{
                            backgroundColor: alpha(BUNKERING_COLOR, 0.045),
                            "&:hover td": {
                              backgroundColor: alpha(BUNKERING_COLOR, 0.08),
                            },
                            "& td": {
                              borderColor: alpha(BUNKERING_COLOR, 0.12),
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 850,
                                color: "#111827",
                                fontSize: "0.83rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {formatTimelineDate(
                                bunkering.date,
                                dateLocale
                              )}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.35,
                                color: "text.secondary",
                                fontSize: "0.74rem",
                                fontWeight: 650,
                                textTransform: "capitalize",
                              }}
                            >
                              {formatTimelineDay(
                                bunkering.date,
                                dateLocale
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  color: BUNKERING_COLOR,
                                  fontSize: "0.83rem",
                                }}
                              >
                                {t("bunkering", "Bunkering")}
                              </Typography>

                              <Typography
                                noWrap
                                sx={{
                                  mt: 0.25,
                                  color: "text.secondary",
                                  fontSize: "0.72rem",
                                  fontWeight: 650,
                                }}
                              >
                                {timelineRow.vessel.name ||
                                  t("unknownVessel", "Unknown vessel")}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell colSpan={5} align="center">
                            <Typography
                              sx={{
                                fontWeight: 950,
                                color: BUNKERING_COLOR,
                                fontSize: "1rem",
                                lineHeight: 1.2,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              +{formatGasoilAmount(bunkering.amount)}
                            </Typography>
                          </TableCell>

                          <TableCell colSpan={4} align="center">
                            <Typography
                              sx={{
                                color: "#111827",
                                fontSize: "0.76rem",
                                fontWeight: 800,
                              }}
                            >
                              {bunkering.supplier ||
                                t("noSupplier", "No supplier")}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={<LocalGasStationOutlinedIcon />}
                              label={t("bunkering", "Bunkering")}
                              sx={{
                                height: 28,
                                borderRadius: "8px",
                                border: `1px solid ${alpha(
                                  BUNKERING_COLOR,
                                  0.2
                                )}`,
                                backgroundColor: alpha(
                                  BUNKERING_COLOR,
                                  0.1
                                ),
                                color: BUNKERING_COLOR,
                                fontWeight: 950,
                                fontSize: "0.68rem",
                                "& .MuiChip-icon": {
                                  color: BUNKERING_COLOR,
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    }

                    const row = timelineRow.row;
                    const statusStyles = getStatusStyles(row.status.key);

                    const translatedStatusLabel = getTranslatedStatusLabel(
                      row.status.key,
                      row.status.label,
                      t
                    );

                    return (
                      <TableRow
                        key={timelineRow.key}
                        hover
                        sx={{
                          "&:hover td": {
                            backgroundColor: "#fbfdff",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 850,
                              color: "#111827",
                              fontSize: "0.83rem",
                              textTransform: "capitalize",
                            }}
                          >
                            {formatDisplayDate(row.dateIso, dateLocale)}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.4,
                              color: "text.secondary",
                              fontSize: "0.76rem",
                              fontWeight: 650,
                              textTransform: "capitalize",
                            }}
                          >
                            {formatDisplayDay(
                              row.dateIso,
                              row.day,
                              dateLocale
                            )}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 950,
                              color: "#111827",
                              fontSize: "0.83rem",
                              lineHeight: 1.25,
                            }}
                          >
                            {row.terminal.name || t("noTerminal")}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.4,
                              color: "text.secondary",
                              fontSize: "0.76rem",
                              fontWeight: 650,
                            }}
                          >
                            {row.terminal.city || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          {renderDateTime(row.planned, dateLocale)}
                        </TableCell>

                        <TableCell align="center">
                          {renderDateTime(row.arrival, dateLocale)}
                        </TableCell>

                        <TableCell align="center">
                          {renderDateTime(row.operationStart, dateLocale)}
                        </TableCell>

                        <TableCell align="center">
                          {renderDateTime(row.operationEnd, dateLocale)}
                        </TableCell>

                        <TableCell align="center">
                          {renderDateTime(row.departure, dateLocale)}
                        </TableCell>

                        <TableCell align="center">
                          <Typography
                            sx={{
                              color: LOADING_COLOR,
                              fontWeight: 950,
                              fontSize: "0.9rem",
                            }}
                          >
                            {row.loading.containers}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography
                            sx={{
                              color: LOADING_COLOR,
                              fontWeight: 950,
                              fontSize: "0.9rem",
                            }}
                          >
                            {row.loading.teu}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography
                            sx={{
                              color: UNLOADING_COLOR,
                              fontWeight: 950,
                              fontSize: "0.9rem",
                            }}
                          >
                            {row.unloading.containers}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography
                            sx={{
                              color: UNLOADING_COLOR,
                              fontWeight: 950,
                              fontSize: "0.9rem",
                            }}
                          >
                            {row.unloading.teu}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            size="small"
                            icon={statusStyles.icon}
                            label={translatedStatusLabel}
                            sx={{
                              height: 28,
                              px: 0.7,
                              borderRadius: "8px",
                              border: `1px solid ${statusStyles.borderColor}`,
                              backgroundColor: statusStyles.backgroundColor,
                              color: statusStyles.color,
                              fontWeight: 950,
                              fontSize: "0.68rem",
                              "& .MuiChip-icon": {
                                color: statusStyles.color,
                                ml: 0.5,
                              },
                              "& .MuiChip-label": {
                                px: 0.8,
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Box sx={{ py: 5 }}>
                        <Typography
                          sx={{
                            fontWeight: 850,
                            color: "text.secondary",
                          }}
                        >
                          {t(
                            "noWeeklyReportActivities",
                            "No weekly report activities found."
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {report ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        backgroundColor: "#f1f7ff",
                        fontWeight: 950,
                        color: "#111827",
                        fontSize: "0.95rem",
                      }}
                    >
                      {t("weeklyTotal")}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#f1f7ff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: LOADING_COLOR,
                          fontWeight: 950,
                        }}
                      >
                        {report.totals.loadingContainers}
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#f1f7ff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: LOADING_COLOR,
                          fontWeight: 950,
                        }}
                      >
                        {report.totals.loadingTeu}
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#f1f7ff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: UNLOADING_COLOR,
                          fontWeight: 950,
                        }}
                      >
                        {report.totals.unloadingContainers}
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#f1f7ff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: UNLOADING_COLOR,
                          fontWeight: 950,
                        }}
                      >
                        {report.totals.unloadingTeu}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{
                        backgroundColor: "#f1f7ff",
                      }}
                    />
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <GasoilYearOverviewDialog
          open={yearOverviewOpen}
          vesselId={yearOverviewVesselId}
          allowAddMeasurement={yearOverviewVesselId !== null}
          onMeasurementSaved={fetchWeeklyReport}
          onClose={() => setYearOverviewOpen(false)}
        />
      </Box>
    </Layout>
  );
};

export default WeeklyOperationsReport;
